import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Moon, Sun } from '@phosphor-icons/react';
import { MobileNav, type MobileNavEintrag } from '../ui/MobileNav';
import { HAUPT_NAV } from '../../../lib/immobilien/sidebarNav';
import { useImmoTheme } from '../../../lib/immobilien/theme';
import { alsModulListe, isItemVisible, routeItemId, type VisibilityContext } from '../../../lib/sidebar/navigation';
import { supabase } from '../../../lib/supabase/client';
import { useSidebarDaten } from '../../admin/useSidebarDaten';
import { SIDEBAR_CATEGORIES, TOP_LEVEL_ITEMS } from '../../admin/sidebarConfig';
import { HAUPT_ICONS } from './ListenModus';
import { OFFENE_STATUS } from '../../../lib/dokumente/ablage';

const ALLE_ITEMS = [...TOP_LEVEL_ITEMS, ...SIDEBAR_CATEGORIES.flatMap(c => c.items)];
/** Feste Einträge der unteren Leiste (Handoff E-Mails §6/Screen 30, Entscheidung 14.10.2026; Tickets ergänzt 16.10.2026; Dienstleister im Mehr-Sheet). */
const LEISTE = ['objekte', 'mieter', 'tickets', 'emails', 'dokumente'] as const;

/** Offene Ablage-Vorschläge (rotes Badge an „Dokumente“). */
export const ABLAGE_GEAENDERT = 'immo-ablage-geaendert';
function useOffeneAblagen(): number {
  const [n, setN] = useState(0);
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    let aktiv = true;
    supabase.from('dokument_ablage_vorschlaege').select('id', { count: 'exact', head: true }).in('status', [...OFFENE_STATUS])
      .then(({ count }) => { if (aktiv) setN(count ?? 0); });
    return () => { aktiv = false; };
  }, [runde]);
  useEffect(() => {
    const neu = () => setRunde(r => r + 1);
    window.addEventListener(ABLAGE_GEAENDERT, neu);
    return () => window.removeEventListener(ABLAGE_GEAENDERT, neu);
  }, []);
  return n;
}

/** Untere Leiste + Mehr-Sheet: Bereiche wie in der Sidebar (Module/Rechte), Badge offene Ablagen. */
export function MobileLeiste({ quer, userRole, allowedModules, onNavigate }: {
  quer: boolean;
  userRole?: string | null;
  allowedModules?: unknown;
  onNavigate: (pfad: string) => void;
}) {
  const { pathname } = useLocation();
  const { activeModules, emailBadge } = useSidebarDaten();
  const offen = useOffeneAblagen();
  const [mehrOffen, setMehrOffen] = useState(false);
  const rolle = (userRole ?? '').toLowerCase();
  const sichtbar = useMemo(() => {
    const ctx: VisibilityContext = { isSuperAdmin: rolle === 'super admin', isTeam: rolle === 'team', activeModules, allowedModules: alsModulListe(allowedModules) };
    return HAUPT_NAV.filter(n => { const meta = ALLE_ITEMS.find(i => i.id === n.id); return meta ? isItemVisible(meta, ctx) : false; });
  }, [rolle, activeModules, allowedModules]);
  const eintrag = (n: (typeof HAUPT_NAV)[number]): MobileNavEintrag => ({ id: n.id, label: n.label, icon: HAUPT_ICONS[n.id], badge: n.id === 'dokumente' ? offen : n.id === 'emails' ? emailBadge : undefined });
  const leiste = LEISTE.map(id => sichtbar.find(n => n.id === id)).filter((n): n is (typeof HAUPT_NAV)[number] => !!n).map(eintrag);
  const pfad = (id: string) => HAUPT_NAV.find(n => n.id === id)?.path ?? '/admin/dashboard';
  return (
    <MobileNav eintraege={leiste} aktivId={routeItemId(pathname)} quer={quer} mehr={sichtbar.map(eintrag)} mehrOffen={mehrOffen} onMehr={setMehrOffen}
      onWahl={id => onNavigate(pfad(id))} />
  );
}

/** Schmale Kopfleiste mobil: App-Icon 32 px, „ImmoSchwede“, zwei runde Knöpfe Hell/Dunkel (40 px). */
export function MobileKopf({ extra }: { extra?: React.ReactNode }) {
  const [theme, setTheme] = useImmoTheme();
  const knopf = (wert: 'light' | 'dark', label: string, IconKomp: typeof Sun) => {
    const an = theme === wert;
    return (
      <button type="button" aria-label={label} aria-pressed={an} onClick={() => setTheme(wert)} className="flex items-center justify-center cursor-pointer"
        style={{ width: 40, height: 40, borderRadius: 20, border: 'none', background: an ? 'var(--acc-fill)' : 'var(--fill2)', color: an ? 'var(--acc-ink)' : 'var(--ink2)' }}>
        <IconKomp weight="fill" size={18} />
      </button>
    );
  };
  return (
    // Platz oben für die schwebende Pille (Timer, Benachrichtigungen, Formulare), die über allen Seiten liegt
    <div className="flex items-center" style={{ gap: 10, marginBottom: 6, paddingTop: 'calc(46px + env(safe-area-inset-top))' }}>
      <img src="/app-icon-192.png" alt="" style={{ width: 32, height: 32, borderRadius: 9 }} />
      <span style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>ImmoSchwede</span>
      {extra}
      {knopf('light', 'Hell', Sun)}
      {knopf('dark', 'Dunkel', Moon)}
    </div>
  );
}
